'use es6';

import PropTypes from 'prop-types';
import { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isEmpty, isEqual } from 'underscore';
import { isCaptchaEnabled, containsEmailField, getIsPaginatedForm } from 'FormsUI/selectors/FormDefinition';
import { getMainFormFieldGroups } from 'FormsUI/selectors/FormFieldsProperties';
import { addFormFieldGroup as addFormFieldGroupFromAction, moveFormFieldIntoNewFormFieldGroup as moveFormFieldIntoNewFormFieldGroupFromAction } from 'FormsUI/actions/FormDragDrop';
import { updateSelectedFormItem as updateSelectedFormItemFromAction, resetSelectedFormItem as resetSelectedFormItemFromAction } from 'FormsUI/actions/FormSelection';
import { removeLegalConsent as removeLegalConsentFromAction } from 'FormsUI/actions/LegalConsent';
import { SpecialFieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { FieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { getLegalConsentOptions, getCurrentSelectedLegalConsentOption } from 'FormsUI/selectors/LegalConsent';
import LegalConsent from 'FormsCommonComponents/lib/LegalConsent';
import { LegalConsentOptions } from 'FormsCommonComponents/constants/LegalConsent';
import CaptchaContainer from './CaptchaContainer';
import FormFieldGroupList from 'FormsUI/components/editor/form/formFields/FormFieldGroupList';
import DropZoneTarget from 'FormsUI/components/editor/form/dropZone/DropZoneTarget';
import SubmitButtonContainer from 'FormsUI/components/editor/form/formFields/SubmitButtonContainer';
import LegalConsentPreviewField from 'FormsUI/components/editor/form/formFields/LegalConsentPreviewField';
import { Form } from 'foundations-components/transitional/components/Form';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { SpecialFieldUrls } from 'FormsUI/constants/SpecialFieldUrls';
import { isLegalConsentSelected } from 'FormsUI/utils/SpecialFields';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const langKeyValidation = suffix => `editor.validation.${suffix}`;
export class FormFieldsPanel extends PureComponent {
  constructor() {
    super();
    this.handleSelectLegalConsent = this.handleSelectLegalConsent.bind(this);
  }
  handleSelectLegalConsent(type) {
    const {
      selectedFormItemType,
      updateSelectedFormItem,
      resetSelectedFormItem
    } = this.props;
    if (isEqual(selectedFormItemType, type)) {
      resetSelectedFormItem({
        updateRoute: true
      });
      return;
    }
    updateSelectedFormItem(type, SpecialFieldUrls.LEGAL_CONSENT);
  }
  maybeRenderFormFields() {
    const {
      formFieldGroups,
      handleRichTextSelect,
      handleFormFieldSelect,
      selectedFormItemType,
      selectedFormItemReference,
      formType
    } = this.props;
    if (isEmpty(formFieldGroups)) {
      return this.renderNoFormFieldsWarningAlert(formFieldGroups);
    }
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(FormFieldGroupList, {
        handleRichTextSelect: handleRichTextSelect,
        handleFormFieldSelect: handleFormFieldSelect,
        formFieldGroups: formFieldGroups,
        isQueued: false,
        selectedFormItemType: selectedFormItemType,
        selectedFormItemReference: selectedFormItemReference,
        formType: formType
      })
    });
  }
  maybeRenderCaptcha() {
    const {
      captchaEnabled,
      handleCaptchaSelect,
      selectedFormItemType,
      selectedFormItemReference
    } = this.props;
    if (!captchaEnabled) {
      return null;
    }
    return /*#__PURE__*/_jsx(CaptchaContainer, {
      selectedFormItemType: selectedFormItemType,
      selectedFormItemReference: selectedFormItemReference,
      handleSelect: handleCaptchaSelect
    });
  }
  maybeRenderDropZoneTarget() {
    const {
      formFieldGroups,
      addFormFieldGroup,
      moveFormFieldIntoNewFormFieldGroup,
      selectedFormItemReference
    } = this.props;
    if (formFieldGroups.length === 0) {
      return /*#__PURE__*/_jsx(DropZoneTarget, {
        addFormFieldGroup: addFormFieldGroup,
        moveFormFieldIntoNewFormFieldGroup: moveFormFieldIntoNewFormFieldGroup,
        formFieldGroupIndex: formFieldGroups.length // append to end of main form field groups
        ,
        disabled: Boolean(selectedFormItemReference),
        isQueued: false
      });
    }
    return null;
  }
  maybeRenderLegalConsentPreviewField() {
    const {
      currentlySelectedLegalConsentOptionType,
      currentLegalConsentOption,
      selectedFormItemType,
      selectedFormItemReference,
      removeLegalConsent
    } = this.props;
    if (isEqual(currentlySelectedLegalConsentOptionType, LegalConsentOptions.NONE)) {
      return null;
    }
    return /*#__PURE__*/_jsx(LegalConsentPreviewField, {
      option: currentLegalConsentOption,
      optionType: currentlySelectedLegalConsentOptionType,
      handleSelect: () => this.handleSelectLegalConsent(SpecialFieldTypes.LEGAL_CONSENT),
      handleDelete: removeLegalConsent,
      isSelected: isLegalConsentSelected({
        selectedFormItemType,
        selectedFormItemReference
      })
    });
  }
  renderNoFormFieldsWarningAlert() {
    return /*#__PURE__*/_jsx(Alert, {
      "data-test-id": "form-field-alert",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: langKeyValidation('noFieldsHeader')
      }),
      type: "danger",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: langKeyValidation('noFieldsMessagePublish')
      })
    });
  }
  renderSubmitButton() {
    const {
      selectedFormItemType,
      handleSubmitSelect,
      selectedFormItemReference
    } = this.props;
    return /*#__PURE__*/_jsx(SubmitButtonContainer, {
      selectedFormItemType: selectedFormItemType,
      selectedFormItemReference: selectedFormItemReference,
      handleSubmitSelect: handleSubmitSelect
    });
  }
  render() {
    return /*#__PURE__*/_jsx(Fragment, {
      children: /*#__PURE__*/_jsxs(Form, {
        padding: "400",
        "data-test-id": "form-fields-panel",
        children: [this.maybeRenderFormFields(), this.maybeRenderDropZoneTarget(), this.maybeRenderLegalConsentPreviewField(), this.maybeRenderCaptcha(), this.renderSubmitButton()]
      })
    });
  }
}
FormFieldsPanel.propTypes = {
  formFieldGroups: PropTypes.array.isRequired,
  handleFormFieldSelect: PropTypes.func.isRequired,
  handleSubmitSelect: PropTypes.func.isRequired,
  handleRichTextSelect: PropTypes.func.isRequired,
  handleCaptchaSelect: PropTypes.func.isRequired,
  selectedFormItemType: PropTypes.oneOf([...Object.values(FieldTypes || {}), ...Object.values(SpecialFieldTypes || {})]),
  selectedFormItemReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  addFormFieldGroup: PropTypes.func.isRequired,
  moveFormFieldIntoNewFormFieldGroup: PropTypes.func.isRequired,
  captchaEnabled: PropTypes.bool.isRequired,
  currentlySelectedLegalConsentOptionType: PropTypes.string.isRequired,
  currentLegalConsentOption: PropTypes.instanceOf(LegalConsent),
  updateSelectedFormItem: PropTypes.func.isRequired,
  resetSelectedFormItem: PropTypes.func.isRequired,
  removeLegalConsent: PropTypes.func.isRequired,
  formType: PropTypes.string,
  fixEditorSuccess: PropTypes.func,
  isPaginatedForm: PropTypes.bool.isRequired
};
const mapStateToProps = state => ({
  formFieldGroups: getMainFormFieldGroups(state),
  captchaEnabled: isCaptchaEnabled(state),
  hasEmailField: containsEmailField(state),
  currentlySelectedLegalConsentOptionType: getCurrentSelectedLegalConsentOption(state),
  currentLegalConsentOption: getLegalConsentOptions(state),
  isPaginatedForm: getIsPaginatedForm(state)
});
const actionCreators = {
  addFormFieldGroup: addFormFieldGroupFromAction,
  moveFormFieldIntoNewFormFieldGroup: moveFormFieldIntoNewFormFieldGroupFromAction,
  updateSelectedFormItem: updateSelectedFormItemFromAction,
  resetSelectedFormItem: resetSelectedFormItemFromAction,
  removeLegalConsent: removeLegalConsentFromAction
};
export default connect(mapStateToProps, actionCreators)(FormFieldsPanel);